import { IMAGES } from "@/site";
import { PageHero, Section, SectionTitle, CTABand } from "@/components/ui-blocks/Blocks";

const VALUES = [
  { title: "Safety", body: "Student safety always comes first. Every training decision should support safer transportation." },
  { title: "Patience", body: "Strong training requires repetition, support, clear explanation, and calm correction." },
  { title: "Accountability", body: "Drivers must understand the seriousness of their role and take ownership of their decisions." },
  { title: "Communication", body: "Professional communication protects students, drivers, schools, families, and transportation teams." },
  { title: "Integrity", body: "Training must be honest, thorough, and practical." },
  { title: "Professionalism", body: "School bus drivers represent their schools, transportation departments, and communities." },
  { title: "Teamwork", body: "Safe transportation depends on drivers, trainers, dispatchers, mechanics, schools, families, and administrators working together." },
];

export default function About() {
  return (
    <>
      <PageHero
        testid="about-hero"
        eyebrow="About"
        title="Coach. Learn. Build."
        description="CLB Solution LLC was created to support safer school transportation through training, coaching, and workforce development."
        image={IMAGES.services}
      />

      <Section testid="about-clb-meaning">
        <SectionTitle eyebrow="The Name" title="What CLB Stands For" />
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            { letter: "C", word: "Coach", body: "Training with patience, structure, accountability, and guidance." },
            { letter: "L", word: "Learn", body: "Helping drivers understand procedures, safety habits, and the reasons behind them." },
            { letter: "B", word: "Build", body: "Building stronger drivers, safer habits, better decision-making, and professional growth." },
          ].map((b) => (
            <div key={b.letter} className="bg-[#09090b] p-10">
              <div className="text-[#FFCC00] font-display text-7xl font-bold leading-none">{b.letter}</div>
              <div className="mt-4 font-display text-2xl font-bold uppercase">{b.word}</div>
              <p className="mt-3 text-white/65 text-sm leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>

        <p className="mt-12 text-white/70 max-w-3xl text-base md:text-lg leading-relaxed">
          CLB Solution focuses on preparing drivers for the real responsibilities of school
          transportation. The goal is not simply to help drivers pass a test. The goal is to help
          create safer, more confident, more professional school bus drivers.
        </p>
      </Section>

      <Section className="bg-[#0c0c0e] border-y border-white/10" testid="about-mission-vision">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="border border-white/10 p-8 md:p-10 bg-[#09090b]">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-4 font-semibold">Mission</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase leading-tight">
              To produce safe, confident, and employment-ready school bus drivers through patient
              instruction, structured coaching, practical training, and safety-focused education.
            </h3>
          </div>
          <div className="border border-white/10 p-8 md:p-10 bg-[#09090b]">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-4 font-semibold">Vision</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase leading-tight">
              To strengthen school transportation by reducing preventable incidents, improving driver
              preparedness, supporting workforce development, and becoming a trusted training and
              staffing solution.
            </h3>
          </div>
        </div>
      </Section>

      <Section testid="about-values">
        <SectionTitle eyebrow="Core Values" title="The principles behind every training session." />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {VALUES.map((v, i) => (
            <div key={v.title} className="bg-[#09090b] p-7">
              <div className="font-mono text-[10px] text-[#FFCC00] mb-3">{String(i + 1).padStart(2, "0")}</div>
              <h4 className="font-display text-xl font-bold uppercase mb-2">{v.title}</h4>
              <p className="text-sm text-white/65 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <CTABand />
    </>
  );
}
